// === AiCourseGenerator === Workflow Architect — multi-scene topic-to-course.
//
// Sits in the CanvasWorkflowPanel header next to AiWorkflowGenerator's
// single-prompt trigger. Renders an icon-only "auto_stories" button which
// opens an inline popover with:
//   - a topic textarea
//   - a numeric scene-count input (1-10, default 3)
//   - a Generate button
//
// Submit dispatches generateFullCourseThunk(topic, sceneCount, locale). The
// thunk loops the architect endpoint sceneCount times and materializes each
// response into the active section's workflow (MVP: all calls land on the
// currently selected scene). Same UI conventions as AiWorkflowGenerator: not
// a modal (popover only, per "no popup over a popup"), Escape / backdrop
// click closes it, return focus to trigger on close.

import React, { useState, useRef, useEffect, useCallback } from "react";
import { connect } from "react-redux";
import { generateFullCourseThunk } from "../../../common/actions";
import { LuBookOpen } from "react-icons/lu";
import "./AiWorkflowGenerator.scss";
import { getContentLanguage } from "../../../common/content_language";

const MIN_SCENES = 1;
const MAX_SCENES = 10;
const DEFAULT_SCENES = 3;

function resolveLocale() {
  return getContentLanguage();
}

function clampSceneCount(n) {
  const x = Number(n);
  if (!Number.isFinite(x)) return DEFAULT_SCENES;
  if (x < MIN_SCENES) return MIN_SCENES;
  if (x > MAX_SCENES) return MAX_SCENES;
  return Math.floor(x);
}

export function AiCourseGenerator({ sectionId, onGenerate }) {
  const [open, setOpen] = useState(false);
  const [topic, setTopic] = useState("");
  const [sceneCount, setSceneCount] = useState(DEFAULT_SCENES);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const triggerRef = useRef(null);
  const topicRef = useRef(null);
  const popoverRef = useRef(null);

  const close = useCallback(() => {
    setOpen(false);
    setError(null);
    if (triggerRef.current && typeof triggerRef.current.focus === "function") {
      triggerRef.current.focus();
    }
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    if (topicRef.current && typeof topicRef.current.focus === "function") {
      topicRef.current.focus();
    }
    const onKey = (e) => {
      if (e.key === "Escape") close();
    };
    const onClick = (e) => {
      if (
        popoverRef.current &&
        !popoverRef.current.contains(e.target) &&
        triggerRef.current &&
        !triggerRef.current.contains(e.target)
      ) {
        close();
      }
    };
    document.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onClick);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onClick);
    };
  }, [open, close]);

  // Section is required to attach generated triggers to a workflow slot.
  // sectionId may legitimately be `0` (course root), so the explicit check.
  const disabled = !sectionId && sectionId !== 0;

  const handleSubmit = async (e) => {
    if (e && typeof e.preventDefault === "function") e.preventDefault();
    const trimmed = topic.trim();
    const n = clampSceneCount(sceneCount);
    if (!trimmed || busy || disabled) return;
    setBusy(true);
    setError(null);
    try {
      await onGenerate(trimmed, n, resolveLocale());
      setTopic("");
      setSceneCount(DEFAULT_SCENES);
      setOpen(false);
      if (triggerRef.current && typeof triggerRef.current.focus === "function") {
        triggerRef.current.focus();
      }
    } catch (err) {
      setError(String((err && err.message) || err) || "Generation failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="ai-workflow-generator ai-workflow-generator--course">
      <button
        ref={triggerRef}
        type="button"
        className="ai-workflow-generator__trigger"
        onClick={() => setOpen((v) => !v)}
        aria-label="Generate full course"
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-disabled={disabled}
        disabled={disabled}
        title="Generate full course"
        data-testid="ai-course-generator-trigger"
      >
        <LuBookOpen size={16} />
      </button>
      {open && (
        <div
          ref={popoverRef}
          className="ai-workflow-generator__popover"
          role="dialog"
          aria-label="Generate full course"
          data-testid="ai-course-generator-popover"
        >
          <form className="ai-workflow-generator__form" onSubmit={handleSubmit} noValidate>
            <label className="ai-workflow-generator__label" htmlFor="ai-course-generator-topic">
              Generate full course
            </label>
            <textarea
              ref={topicRef}
              id="ai-course-generator-topic"
              className="ai-workflow-generator__textarea"
              rows={4}
              placeholder="e.g. Fire safety induction for office staff"
              value={topic}
              onChange={(e) => setTopic(e.target.value)}
              disabled={busy}
              data-testid="ai-course-generator-topic"
            />
            <label
              className="ai-workflow-generator__label"
              htmlFor="ai-course-generator-scene-count"
            >
              Number of scenes
            </label>
            <input
              id="ai-course-generator-scene-count"
              className="ai-workflow-generator__number"
              type="number"
              min={MIN_SCENES}
              max={MAX_SCENES}
              step={1}
              value={sceneCount}
              onChange={(e) => setSceneCount(e.target.value)}
              disabled={busy}
              data-testid="ai-course-generator-scene-count"
            />
            {error && (
              <div className="ai-workflow-generator__error" role="alert">
                {error}
              </div>
            )}
            <div className="ai-workflow-generator__actions">
              <button
                type="button"
                className="ai-workflow-generator__cancel"
                onClick={close}
                disabled={busy}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="ai-workflow-generator__submit"
                disabled={busy || !topic.trim() || disabled}
                data-testid="ai-course-generator-submit"
              >
                {busy ? "Generating…" : "Generate"}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

const mapDispatch = (dispatch) => ({
  onGenerate: (topic, sceneCount, locale) =>
    dispatch(generateFullCourseThunk(topic, sceneCount, locale)),
});

export default connect(null, mapDispatch)(AiCourseGenerator);
